import { PageLayout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useInstitutionLocations } from "@/hooks/useInstitutionLocations";
import {
  useCreateInstitutionStaff,
  useInstitutionStaffById,
  useUpdateInstitutionStaff,
} from "@/hooks/useInstitutionStaff";
import { useRoles } from "@/hooks/useRoles";
import { useUsers } from "@/hooks/useUsers";
import { useCreateInstitutionStaffForm, usePermissions } from "@niutraan/hooks";
import { PermissionEnum } from "@niutraan/shared-dto";
import { Book } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

export default function InstitutionStaffDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();

  const [isFormInitialized, setIsFormInitialized] = useState(false);

  const institutionId = id || "new";
  const roles = useRoles();
  const { data: users, isLoading: isLoadingUsers } = useUsers();
  const institutionLocations = useInstitutionLocations();
  const canCreateLocations = hasPermission(
    PermissionEnum.INSTITUTION_LOCATION_WRITE,
  );
  const canUpdateLocations = hasPermission(
    PermissionEnum.INSTITUTION_LOCATION_UPDATE,
  );
  const { form, staffData, handleSubmit, isNewStaff, isSubmitting } =
    useCreateInstitutionStaffForm(institutionId, {
      useInstitutionStaffById,
      useCreateInstitutionStaff,
      useUpdateInstitutionStaff,
      onSuccess: () => {
        toast.success(
          isNewStaff
            ? "Institution staff created successfully"
            : "Institution staff updated successfully",
        );
        navigate("/admin/institutions/staff");
      },
      onError: (error: any) =>
        toast.error(`Failed to save institution: ${(error as Error).message}`),
    });

  const { control } = form as any;

  // Populate form when  data is loaded
  useEffect(() => {
    if (
      !isNewStaff &&
      staffData &&
      !isFormInitialized &&
      institutionLocations?.data?.length
    ) {
      const location = institutionLocations?.data?.find(
        (l) => l.id === staffData.institutionLocation?.id,
      );
      console.log(location);
      form.reset({
        userId: staffData.userId || "",
        roleId: staffData.role?.id || "",
        institutionLocationId: location?.id || "",
        isActive: staffData.isActive ?? false,
      });

      setIsFormInitialized(true);
    }
  }, [
    staffData,
    isNewStaff,
    form,
    isFormInitialized,
    institutionLocations?.data,
  ]);

  // Show unauthorized state if user doesn't have permission
  if (
    (isNewStaff && !canCreateLocations) ||
    (!isNewStaff && !canUpdateLocations)
  ) {
    return (
      <PageLayout title="Unauthorized">
        <div className="flex flex-col items-center justify-center min-h-[40vh] text-center">
          <div className="p-4 rounded-full bg-muted mb-4">
            <Book className="h-12 w-12 text-muted-foreground" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-muted-foreground max-w-md mb-6">
            You do not have permission to {isNewStaff ? "create" : "update"}{" "}
            Institution.
          </p>
          <Button asChild>
            <Link to="/admin/institutions/staff">Go Back</Link>
          </Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={isNewStaff ? "Add Instution Staff" : "Edit Instution Staff"}
      description={
        isNewStaff
          ? "Create a new Instution Staff"
          : `Editing: ${staffData?.role?.name}`
      }
      actions={
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/admin/institutions/staff">Cancel</Link>
          </Button>
          <Button onClick={handleSubmit}>
            {isSubmitting ? "Saving..." : "Save Instution"}
          </Button>
        </div>
      }
      breadcrumbs={[
        { label: "Admin", href: "/admin" },
        { label: "Institution", href: "/admin/institutions/staff" },
        {
          label: isNewStaff ? "Add Instution Staff" : "Edit Instution Staff",
        },
      ]}
    >
      <Form {...form}>
        <form onSubmit={handleSubmit} className="space-y-8">
          <Card>
            <CardHeader>
              <CardTitle>Institution Staff Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={control}
                name="userId"
                render={({ field }) => (
                  <FormItem key={String(field.value)}>
                    <FormLabel>User *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={String(field.value)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select user" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {isLoadingUsers ? (
                          <div className="flex items-center justify-center p-2">
                            <Skeleton className="h-5 w-full" />
                          </div>
                        ) : users && users?.data?.length > 0 ? (
                          users?.data?.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.firstName} {u.lastName} - {u.phoneNumber}
                            </SelectItem>
                          ))
                        ) : (
                          <div className="p-2 text-sm text-muted-foreground">
                            No users available
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage className="text-left" />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="roleId"
                render={({ field }) => (
                  <FormItem key={String(field.value)}>
                    <FormLabel>Role *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={String(field.value)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {isLoadingUsers ? (
                          <div className="flex items-center justify-center p-2">
                            <Skeleton className="h-5 w-full" />
                          </div>
                        ) : roles ? (
                          roles?.data?.map((r) => (
                            <SelectItem key={r.id} value={r.id}>
                              {r.name} {"\n"} ( {r.description} )
                            </SelectItem>
                          ))
                        ) : (
                          <div className="p-2 text-sm text-muted-foreground">
                            No roles available
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage className="text-left" />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="institutionLocationId"
                render={({ field }) => (
                  <FormItem key={String(field.value)}>
                    <FormLabel>Institution Location *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select institution location" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {institutionLocations ? (
                          institutionLocations?.data?.map((location) => (
                            <SelectItem key={location.id} value={location.id}>
                              {location.name}
                            </SelectItem>
                          ))
                        ) : (
                          <div className="p-2 text-sm text-muted-foreground">
                            No location available
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage className="text-left" />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Active Status</FormLabel>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
        </form>
      </Form>
    </PageLayout>
  );
}
